import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { MARKETING_API_BASE_URL, getMarketingApiHeaders } from '../config/api';

interface Announcement {
  _id: string;
  title: string;
  type: string;
  status: string;
  eventConfig?: {
    categoryKey?: string;
    milestones?: { value: number; label: string }[];
    badge?: { name: string };
  } | null;
  publishedAt?: string;
  expiresAt?: string;
}

const statusColor = (status: string) => {
  const s = status.toLowerCase();
  if (s === 'active' || s === 'published') return 'bg-emerald-500/20 text-emerald-400';
  if (s === 'draft') return 'bg-yellow-500/20 text-yellow-400';
  if (s === 'archived') return 'bg-gray-500/20 text-gray-400';
  return 'bg-white/10 text-gray-400';
};

const AnnouncementList: React.FC = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await fetch(`${MARKETING_API_BASE_URL}/announcements?limit=100&offset=0`, {
          headers: getMarketingApiHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setAnnouncements(json.data?.announcements || json.announcements || (Array.isArray(json.data) ? json.data : []));
      } catch (err) {
        toast.error('Failed to load announcements: ' + (err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const filtered = announcements.filter((a) =>
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    (a.eventConfig?.categoryKey || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Announcement Assets</h1>
            <p className='text-sm text-gray-400 mt-1'>
              Select an announcement to manage its event assets (thumbnails, badges, milestones, gifts).
            </p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search by title or category key…'
            className='w-full sm:w-72 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm'
          />
        </div>

        {loading ? (
          <div className='flex items-center justify-center py-24'>
            <div className='w-8 h-8 rounded-full border-2 border-white/20 border-t-[#FF9C15] animate-spin' />
          </div>
        ) : filtered.length === 0 ? (
          <div className='text-center py-24 text-gray-500'>
            {search ? 'No announcements match your search.' : 'No announcements found.'}
          </div>
        ) : (
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-white/10'>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider'>Title</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell'>Category Key</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell'>Type</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider'>Status</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell'>Assets</th>
                  <th className='px-4 py-3' />
                </tr>
              </thead>
              <tbody className='divide-y divide-white/5'>
                {filtered.map((a) => (
                  <tr key={a._id} className='hover:bg-white/5 transition-colors'>
                    <td className='px-4 py-3 text-white font-medium'>{a.title}</td>
                    <td className='px-4 py-3 hidden sm:table-cell'>
                      {a.eventConfig?.categoryKey ? (
                        <span className='font-mono text-xs px-2 py-1 bg-[#FF9C15]/10 text-[#FF9C15] rounded'>
                          {a.eventConfig.categoryKey}
                        </span>
                      ) : (
                        <span className='text-gray-600 text-xs'>—</span>
                      )}
                    </td>
                    <td className='px-4 py-3 hidden md:table-cell text-gray-400 capitalize'>{a.type}</td>
                    <td className='px-4 py-3'>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${statusColor(a.status)}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className='px-4 py-3 hidden lg:table-cell text-gray-400 text-xs'>
                      {a.eventConfig?.categoryKey ? (
                        <span className='flex items-center gap-2'>
                          {a.eventConfig.milestones?.length ? (
                            <span>{a.eventConfig.milestones.length} milestone{a.eventConfig.milestones.length !== 1 ? 's' : ''}</span>
                          ) : null}
                          {a.eventConfig.badge ? <span>badge</span> : null}
                          {!a.eventConfig.milestones?.length && !a.eventConfig.badge ? '—' : ''}
                        </span>
                      ) : (
                        <span className='text-gray-600'>No event config</span>
                      )}
                    </td>
                    <td className='px-4 py-3 text-right'>
                      <button
                        onClick={() => navigate(`/announcements/${a._id}/assets`)}
                        disabled={!a.eventConfig?.categoryKey}
                        title={!a.eventConfig?.categoryKey ? 'Set a categoryKey in Event Config first' : 'Manage assets'}
                        className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-30 disabled:cursor-not-allowed'
                      >
                        Manage Assets
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementList;
